"use strict";

class KosTimepicker {
	static CHEVRON_UP_SVG = `<svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"/></svg>`;
	static CHEVRON_DOWN_SVG = `<svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>`;
	static CLOCK_SVG = `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;
	static ITEM_HEIGHT = 44;
	static PADDING_COUNT = 2;

	constructor(inputEl) {
		this._input = inputEl;
		this._min = inputEl.dataset.min || null;
		this._max = inputEl.dataset.max || null;
		this._step = parseInt(inputEl.dataset.step || '5', 10);
		this._hourFormat = parseInt(inputEl.dataset.hourFormat || '24', 10);
		this._hour = null;
		this._minute = null;
		this._period = 'AM';
		this._open = false;
		this._wrapper = null;
		this._dropdown = null;
		this._debounceTimer = null;
		this._mobileOpen = false;
		this._mobileOverlay = null;
		this._mobileSheet = null;
		this._mobileHistoryPushed = false;
		this._popstateHandler = null;
		this._overlayClickHandler = null;

		if (this._isMobile()) {
			this._initMobileWheel();
			return;
		}

		this._build();
		this._bindEvents();
		if (inputEl.value) {
			this._parseInputValue(inputEl.value);
		}
	}

	_isMobile() {
		return window.matchMedia('(pointer: coarse)').matches || ('ontouchstart' in window);
	}

	_initMobileWheel() {
		this._buildInputWrapper();
		this._input.readOnly = true;
		this._input.addEventListener('click', () => this._openMobileSheet());
		if (this._input.value) {
			this._parseInputValue(this._input.value);
		}
		this._mobileOverlay = document.createElement('div');
		this._mobileOverlay.className = 'kos-timepicker__mobile-overlay';
		this._mobileSheet = this._buildMobileSheet();
		document.body.appendChild(this._mobileOverlay);
		document.body.appendChild(this._mobileSheet);
	}

	_buildMobileSheet() {
		const sheet = document.createElement('div');
		sheet.className = 'kos-timepicker__mobile-sheet';

		const label = this._input.dataset.mobileLabel;
		if (label) {
			const labelEl = document.createElement('div');
			labelEl.className = 'kos-timepicker__sheet-label';
			labelEl.textContent = label;
			sheet.appendChild(labelEl);
		}

		const header = document.createElement('div');
		header.className = 'kos-timepicker__sheet-header';

		const title = document.createElement('span');
		title.className = 'kos-timepicker__sheet-title';
		title.textContent = 'Select Time';
		header.appendChild(title);

		const applyBtn = document.createElement('button');
		applyBtn.type = 'button';
		applyBtn.className = 'kos-timepicker__sheet-apply';
		applyBtn.textContent = 'Done';
		applyBtn.addEventListener('click', () => this._closeMobileSheet(true));
		header.appendChild(applyBtn);

		sheet.appendChild(header);

		const wheelsContainer = document.createElement('div');
		wheelsContainer.className = 'kos-timepicker__wheels-container';

		const highlight = document.createElement('div');
		highlight.className = 'kos-timepicker__wheel-highlight';
		wheelsContainer.appendChild(highlight);

		const fadeTop = document.createElement('div');
		fadeTop.className = 'kos-timepicker__wheel-fade-top';
		wheelsContainer.appendChild(fadeTop);

		const fadeBottom = document.createElement('div');
		fadeBottom.className = 'kos-timepicker__wheel-fade-bottom';
		wheelsContainer.appendChild(fadeBottom);

		wheelsContainer.appendChild(this._buildWheel('hour'));

		const sep = document.createElement('div');
		sep.className = 'kos-timepicker__wheel-sep';
		sep.textContent = ':';
		wheelsContainer.appendChild(sep);

		wheelsContainer.appendChild(this._buildWheel('minute'));

		if (this._hourFormat === 12) {
			wheelsContainer.appendChild(this._buildWheel('period'));
		}

		sheet.appendChild(wheelsContainer);
		return sheet;
	}

	_buildWheel(type) {
		const wheel = document.createElement('div');
		wheel.className = 'kos-timepicker__wheel';
		wheel.dataset.wheelType = type;

		let values, displayFn, disabledSet = null;
		if (type === 'hour') {
			values = this._hourFormat === 12 ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] : this._validHours();
			displayFn = v => this._pad(v);
			if (this._hourFormat !== 12 && (this._min || this._max)) {
				const permitted = new Set(this._permittedHours());
				disabledSet = new Set();
				values.forEach((v, i) => { if (!permitted.has(v)) disabledSet.add(i); });
			}
		} else if (type === 'minute') {
			values = this._validMinutes();
			displayFn = v => this._pad(v);
		} else {
			values = ['AM', 'PM'];
			displayFn = v => v;
		}

		wheel._values = values;
		wheel._disabledSet = disabledSet;

		const list = document.createElement('div');
		list.className = 'kos-timepicker__wheel-list';
		list._ty = 0;

		for (let i = 0; i < KosTimepicker.PADDING_COUNT; i++) {
			const ghost = document.createElement('div');
			ghost.className = 'kos-timepicker__wheel-item kos-timepicker__wheel-item--ghost';
			list.appendChild(ghost);
		}

		values.forEach((v, i) => {
			const item = document.createElement('div');
			item.className = 'kos-timepicker__wheel-item';
			if (disabledSet && disabledSet.has(i)) item.classList.add('kos-timepicker__wheel-item--disabled');
			item.textContent = displayFn(v);
			list.appendChild(item);
		});

		for (let i = 0; i < KosTimepicker.PADDING_COUNT; i++) {
			const ghost = document.createElement('div');
			ghost.className = 'kos-timepicker__wheel-item kos-timepicker__wheel-item--ghost';
			list.appendChild(ghost);
		}

		wheel.appendChild(list);
		this._initWheelTouch(wheel);
		return wheel;
	}

	_getInitialIndex(type) {
		if (type === 'hour') {
			if (this._hourFormat === 12) {
				const displayH = this._hour === 0 ? 12 : (this._hour > 12 ? this._hour - 12 : this._hour);
				return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].indexOf(displayH);
			}
			return this._validHours().indexOf(this._hour);
		}
		if (type === 'minute') return this._validMinutes().indexOf(this._minute);
		return this._period === 'AM' ? 0 : 1;
	}

	_positionWheelAt(wheelEl, index, animated = false) {
		const list = wheelEl.querySelector('.kos-timepicker__wheel-list');
		const targetTy = -index * KosTimepicker.ITEM_HEIGHT;
		if (animated) {
			list.style.transition = 'transform 0.2s ease-out';
			setTimeout(() => { list.style.transition = ''; }, 220);
		} else {
			list.style.transition = '';
		}
		list._ty = targetTy;
		list.style.transform = `translateY(${targetTy}px)`;
	}

	_initWheelTouch(wheelEl) {
		const list = wheelEl.querySelector('.kos-timepicker__wheel-list');
		let startY = 0;
		let startTy = 0;
		let velBuffer = [];

		wheelEl.addEventListener('touchstart', (e) => {
			e.preventDefault();
			if (list._rafId) cancelAnimationFrame(list._rafId);
			list.style.transition = '';
			startY = e.touches[0].clientY;
			startTy = list._ty ?? 0;
			velBuffer = [{ y: startY, t: Date.now() }];
		}, { passive: false });

		wheelEl.addEventListener('touchmove', (e) => {
			e.preventDefault();
			const currentY = e.touches[0].clientY;
			list._ty = startTy + (currentY - startY);
			list.style.transform = `translateY(${list._ty}px)`;
			velBuffer.push({ y: currentY, t: Date.now() });
			if (velBuffer.length > 4) velBuffer.shift();
		}, { passive: false });

		wheelEl.addEventListener('touchend', () => {
			const last = velBuffer[velBuffer.length - 1];
			const prev = velBuffer[Math.max(0, velBuffer.length - 3)];
			const dt = last.t - prev.t;
			const velocity = dt > 0 ? (last.y - prev.y) / dt * 16 : 0;
			this._momentumAndSnap(wheelEl, velocity);
		});
	}

	_nearestPermittedIndex(wheelEl, idx) {
		const disabled = wheelEl._disabledSet;
		if (!disabled || disabled.size === 0 || !disabled.has(idx)) return idx;
		const n = wheelEl._values.length;
		for (let delta = 1; delta < n; delta++) {
			if (idx + delta < n && !disabled.has(idx + delta)) return idx + delta;
			if (idx - delta >= 0 && !disabled.has(idx - delta)) return idx - delta;
		}
		return idx;
	}

	_momentumAndSnap(wheelEl, velocity) {
		const list = wheelEl.querySelector('.kos-timepicker__wheel-list');
		const values = wheelEl._values;
		const minTy = -(values.length - 1) * KosTimepicker.ITEM_HEIGHT;
		const maxTy = 0;

		const animate = () => {
			velocity *= 0.92;
			list._ty += velocity;
			list.style.transform = `translateY(${list._ty}px)`;
			if (Math.abs(velocity) > 0.5 && list._ty > minTy - KosTimepicker.ITEM_HEIGHT && list._ty < maxTy + KosTimepicker.ITEM_HEIGHT) {
				list._rafId = requestAnimationFrame(animate);
			} else {
				const clampedTy = Math.max(minTy, Math.min(maxTy, list._ty));
				const rawIdx = Math.max(0, Math.min(values.length - 1, Math.round(-clampedTy / KosTimepicker.ITEM_HEIGHT)));
				const idx = this._nearestPermittedIndex(wheelEl, rawIdx);
				this._positionWheelAt(wheelEl, idx, true);
				this._readWheelsToState();
				this._enforceMinMax();
			}
		};
		list._rafId = requestAnimationFrame(animate);
	}

	_enforceMinMax() {
		if (!this._min && !this._max) return;
		if (this._isTimeAllowed(this._hour, this._minute)) return;
		this._snapToNearest();
		this._mobileSheet.querySelectorAll('.kos-timepicker__wheel').forEach(wheel => {
			this._positionWheelAt(wheel, Math.max(0, this._getInitialIndex(wheel.dataset.wheelType)), true);
		});
	}

	_snapToNearest() {
		const t = this._toMinutes(this._hour, this._minute);
		const minMins = this._min ? this._toMinutes(...this._min.split(':').map(Number)) : null;
		const maxMins = this._max ? this._toMinutes(...this._max.split(':').map(Number)) : null;

		if (minMins !== null && maxMins !== null && maxMins < minMins) {
			this._snapToFirstValid();
			return;
		}

		if (minMins !== null && t < minMins) {
			[this._hour, this._minute] = this._min.split(':').map(Number);
		} else if (maxMins !== null && t > maxMins) {
			[this._hour, this._minute] = this._max.split(':').map(Number);
		}

		if (this._hourFormat === 12) {
			this._period = this._hour >= 12 ? 'PM' : 'AM';
		}
	}

	_readWheelsToState() {
		let readHour = null, readMinute = null, readPeriod = null;

		this._mobileSheet.querySelectorAll('.kos-timepicker__wheel').forEach(wheel => {
			const list = wheel.querySelector('.kos-timepicker__wheel-list');
			const ty = list._ty ?? 0;
			const idx = Math.max(0, Math.min(wheel._values.length - 1, Math.round(-ty / KosTimepicker.ITEM_HEIGHT)));
			const type = wheel.dataset.wheelType;
			if (type === 'hour') readHour = wheel._values[idx];
			else if (type === 'minute') readMinute = wheel._values[idx];
			else if (type === 'period') readPeriod = wheel._values[idx];
		});

		if (readMinute !== null) this._minute = readMinute;
		if (this._hourFormat === 12 && readHour !== null) {
			const period = readPeriod ?? this._period;
			this._period = period;
			this._hour = period === 'PM'
				? (readHour === 12 ? 12 : readHour + 12)
				: (readHour === 12 ? 0 : readHour);
		} else if (readHour !== null) {
			this._hour = readHour;
		}
	}

	_openMobileSheet() {
		if (this._mobileOpen) return;
		if (this._input.value) {
			this._parseInputValue(this._input.value);
		}
		if (this._hour === null) this._snapToFirstValid();

		this._mobileSheet.querySelectorAll('.kos-timepicker__wheel').forEach(wheel => {
			const idx = this._getInitialIndex(wheel.dataset.wheelType);
			this._positionWheelAt(wheel, Math.max(0, idx));
		});

		this._mobileOverlay.classList.add('kos-timepicker__mobile-overlay--open');
		requestAnimationFrame(() => {
			this._mobileSheet.classList.add('kos-timepicker__mobile-sheet--open');
		});
		this._mobileOpen = true;

		this._mobileHistoryPushed = true;
		history.pushState({ kosTimepickerOpen: true }, '');

		this._popstateHandler = () => {
			this._mobileHistoryPushed = false;
			this._closeMobileSheet(false);
		};
		this._overlayClickHandler = () => this._closeMobileSheet(false);
		window.addEventListener('popstate', this._popstateHandler);
		this._mobileOverlay.addEventListener('click', this._overlayClickHandler);
	}

	_closeMobileSheet(commit = false) {
		if (!this._mobileOpen) return;
		this._mobileOpen = false;

		if (commit) {
			this._readWheelsToState();
			this._commitValue();
		}

		this._mobileSheet.classList.remove('kos-timepicker__mobile-sheet--open');
		setTimeout(() => {
			this._mobileOverlay.classList.remove('kos-timepicker__mobile-overlay--open');
		}, 300);

		window.removeEventListener('popstate', this._popstateHandler);
		this._mobileOverlay.removeEventListener('click', this._overlayClickHandler);

		if (this._mobileHistoryPushed) {
			this._mobileHistoryPushed = false;
			history.back();
		}
	}

	_parseInputValue(val) {
		const match = val.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
		if (!match) return;
		let h = parseInt(match[1], 10);
		const m = parseInt(match[2], 10);
		if (match[3]) {
			this._period = match[3].toUpperCase();
			if (this._period === 'PM' && h < 12) h += 12;
			if (this._period === 'AM' && h === 12) h = 0;
		}
		this._hour = h;
		this._minute = m;
	}

	_toMinutes(h, m) {
		return h * 60 + m;
	}

	_validMinutes() {
		const mins = [];
		for (let m = 0; m < 60; m += this._step) {
			mins.push(m);
		}
		return mins;
	}

	_validHours() {
		const hours = [];
		for (let h = (this._hourFormat === 12 ? 1 : 0); h < (this._hourFormat === 12 ? 13 : 24); h++) {
			hours.push(h);
		}
		return hours;
	}

	_permittedHours() {
		if (!this._min && !this._max) return this._validHours();
		const minutes = this._validMinutes();
		return this._validHours().filter(h => minutes.some(m => this._isTimeAllowed(h, m)));
	}

	_isTimeAllowed(h, m) {
		if (!this._min && !this._max) return true;
		const t = this._toMinutes(h, m);
		if (this._min && !this._max) {
			return t >= this._toMinutes(...this._min.split(':').map(Number));
		}
		if (!this._min && this._max) {
			return t <= this._toMinutes(...this._max.split(':').map(Number));
		}
		const minMins = this._toMinutes(...this._min.split(':').map(Number));
		const maxMins = this._toMinutes(...this._max.split(':').map(Number));
		if (maxMins < minMins) {
			return t >= minMins || t <= maxMins;
		}
		return t >= minMins && t <= maxMins;
	}

	_snapToFirstValid() {
		const hours = this._validHours();
		const minutes = this._validMinutes();
		for (const h of hours) {
			for (const m of minutes) {
				if (this._isTimeAllowed(h, m)) {
					this._hour = h;
					this._minute = m;
					return;
				}
			}
		}
		this._hour = hours[0];
		this._minute = minutes[0];
	}

	_prevHour() {
		const hours = this._validHours();
		const idx = hours.indexOf(this._hour);
		return hours[(idx - 1 + hours.length) % hours.length];
	}

	_nextHour() {
		const hours = this._validHours();
		const idx = hours.indexOf(this._hour);
		return hours[(idx + 1) % hours.length];
	}

	_prevMinute() {
		const mins = this._validMinutes();
		const idx = mins.indexOf(this._minute);
		return mins[(idx - 1 + mins.length) % mins.length];
	}

	_nextMinute() {
		const mins = this._validMinutes();
		const idx = mins.indexOf(this._minute);
		return mins[(idx + 1) % mins.length];
	}

	_pad(n) {
		return String(n).padStart(2, '0');
	}

	_displayHour(h) {
		if (this._hourFormat === 12) {
			if (h === 0) return this._pad(12);
			if (h > 12) return this._pad(h - 12);
			return this._pad(h);
		}
		return this._pad(h);
	}

	_buildInputWrapper() {
		const wrapper = document.createElement('div');
		wrapper.className = 'kos-timepicker';

		const inputWrapper = document.createElement('div');
		inputWrapper.className = 'kos-timepicker__input-wrapper';

		this._input.parentNode.insertBefore(wrapper, this._input);
		inputWrapper.appendChild(this._input);

		const iconEl = document.createElement('span');
		iconEl.className = 'kos-timepicker__icon';
		iconEl.innerHTML = KosTimepicker.CLOCK_SVG;
		inputWrapper.appendChild(iconEl);

		wrapper.appendChild(inputWrapper);
		this._wrapper = wrapper;
	}

	_build() {
		this._buildInputWrapper();

		this._dropdown = document.createElement('div');
		this._dropdown.className = 'kos-timepicker__dropdown';
		this._wrapper.appendChild(this._dropdown);

		this._renderDropdown();
	}

	_renderDropdown() {
		const dd = this._dropdown;
		dd.innerHTML = '';

		dd.appendChild(this._buildColumn('hour'));

		const sep = document.createElement('div');
		sep.className = 'kos-timepicker__separator';
		sep.textContent = ':';
		dd.appendChild(sep);

		dd.appendChild(this._buildColumn('minute'));

		if (this._hourFormat === 12) {
			dd.appendChild(this._buildPeriodColumn());
		}
	}

	_buildColumn(type) {
		const col = document.createElement('div');
		col.className = 'kos-timepicker__column';

		const btnUp = document.createElement('button');
		btnUp.type = 'button';
		btnUp.className = 'kos-timepicker__arrow';
		btnUp.innerHTML = KosTimepicker.CHEVRON_UP_SVG;
		btnUp.addEventListener('click', () => {
			if (type === 'hour') this._stepHour(-1);
			else this._stepMinute(-1);
		});
		col.appendChild(btnUp);

		const prev = document.createElement('div');
		prev.className = 'kos-timepicker__item kos-timepicker__item--prev';
		prev.addEventListener('click', () => {
			if (type === 'hour') this._stepHour(-1);
			else this._stepMinute(-1);
		});

		const curr = document.createElement('div');
		curr.className = 'kos-timepicker__item kos-timepicker__item--active';

		const next = document.createElement('div');
		next.className = 'kos-timepicker__item kos-timepicker__item--next';
		next.addEventListener('click', () => {
			if (type === 'hour') this._stepHour(1);
			else this._stepMinute(1);
		});

		col.appendChild(prev);
		col.appendChild(curr);
		col.appendChild(next);

		const btnDown = document.createElement('button');
		btnDown.type = 'button';
		btnDown.className = 'kos-timepicker__arrow';
		btnDown.innerHTML = KosTimepicker.CHEVRON_DOWN_SVG;
		btnDown.addEventListener('click', () => {
			if (type === 'hour') this._stepHour(1);
			else this._stepMinute(1);
		});
		col.appendChild(btnDown);

		col.dataset.colType = type;
		return col;
	}

	_buildPeriodColumn() {
		const col = document.createElement('div');
		col.className = 'kos-timepicker__period-col';

		['AM', 'PM'].forEach(p => {
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'kos-timepicker__period-btn' + (this._period === p ? ' kos-timepicker__period-btn--active' : '');
			btn.textContent = p;
			btn.dataset.period = p;
			btn.addEventListener('click', () => this._setPeriod(p));
			col.appendChild(btn);
		});

		return col;
	}

	_updateDropdown() {
		if (this._hour === null) return;

		const cols = this._dropdown.querySelectorAll('[data-col-type]');
		cols.forEach(col => {
			const type = col.dataset.colType;
			const prev = col.querySelector('.kos-timepicker__item--prev');
			const curr = col.querySelector('.kos-timepicker__item--active');
			const next = col.querySelector('.kos-timepicker__item--next');

			if (type === 'hour') {
				prev.textContent = this._displayHour(this._prevHour());
				curr.textContent = this._displayHour(this._hour);
				next.textContent = this._displayHour(this._nextHour());
			} else {
				prev.textContent = this._pad(this._prevMinute());
				curr.textContent = this._pad(this._minute);
				next.textContent = this._pad(this._nextMinute());
			}
		});

		if (this._hourFormat === 12) {
			const periodBtns = this._dropdown.querySelectorAll('.kos-timepicker__period-btn');
			periodBtns.forEach(btn => {
				btn.classList.toggle('kos-timepicker__period-btn--active', btn.dataset.period === this._period);
			});
		}
	}

	_stepHour(delta) {
		const hours = this._validHours();
		const mins = this._validMinutes();
		let idx = hours.indexOf(this._hour);
		let attempts = 0;
		do {
			idx = (idx + delta + hours.length) % hours.length;
			attempts++;
		} while (!this._isTimeAllowed(hours[idx], this._minute) && attempts < hours.length);

		if (!this._isTimeAllowed(hours[idx], this._minute)) {
			for (const m of mins) {
				if (this._isTimeAllowed(hours[idx], m)) {
					this._minute = m;
					break;
				}
			}
		}

		this._hour = hours[idx];
		if (this._hourFormat === 12) {
			this._period = this._hour >= 12 ? 'PM' : 'AM';
		}
		this._updateDropdown();
		this._commitValue();
	}

	_stepMinute(delta) {
		const mins = this._validMinutes();
		const hours = this._validHours();
		let idx = mins.indexOf(this._minute);
		let attempts = 0;
		do {
			idx = (idx + delta + mins.length) % mins.length;
			attempts++;
		} while (!this._isTimeAllowed(this._hour, mins[idx]) && attempts < mins.length);

		if (!this._isTimeAllowed(this._hour, mins[idx])) {
			for (const h of (delta > 0 ? hours : [...hours].reverse())) {
				for (const m of (delta > 0 ? mins : [...mins].reverse())) {
					if (this._isTimeAllowed(h, m)) {
						this._hour = h;
						this._minute = m;
						this._updateDropdown();
						this._commitValue();
						return;
					}
				}
			}
			return;
		}

		this._minute = mins[idx];
		this._updateDropdown();
		this._commitValue();
	}

	_setPeriod(p) {
		if (this._period === p) return;
		this._period = p;
		if (p === 'PM' && this._hour < 12) this._hour += 12;
		if (p === 'AM' && this._hour >= 12) this._hour -= 12;
		this._updateDropdown();
		this._commitValue();
	}

	_commitValue() {
		const display = this._hourFormat === 12
			? `${this._displayHour(this._hour)}:${this._pad(this._minute)} ${this._period}`
			: `${this._pad(this._hour)}:${this._pad(this._minute)}`;
		this._input.value = display;
		clearTimeout(this._debounceTimer);
		this._debounceTimer = setTimeout(() => {
			this._input.dispatchEvent(new Event('change', { bubbles: true }));
			this._input.dispatchEvent(new Event('input', { bubbles: true }));
		}, 800);
	}

	_bindEvents() {
		this._input.addEventListener('click', () => this.open());
		this._input.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this.toggle();
			}
			if (e.key === 'Escape') this.close();
		});

		document.addEventListener('click', (e) => {
			if (this._open && !this._wrapper.contains(e.target)) {
				this.close();
			}
		});
	}

	open() {
		if (this._open) return;
		if (this._input.value) {
			this._parseInputValue(this._input.value);
			if (this._hour !== null && !this._isTimeAllowed(this._hour, this._minute)) {
				this._hour = null;
			}
		}
		if (this._hour === null) this._snapToFirstValid();
		this._updateDropdown();
		this._dropdown.classList.add('kos-timepicker__dropdown--open');
		this._open = true;
	}

	close() {
		if (!this._open) return;
		this._dropdown.classList.remove('kos-timepicker__dropdown--open');
		this._open = false;
	}

	toggle() {
		this._open ? this.close() : this.open();
	}

	setValue(timeStr) {
		this._parseInputValue(timeStr);
		if (this._hour !== null) {
			this._updateDropdown();
			this._commitValue();
		}
	}

	clear() {
		this._hour = null;
		this._minute = null;
		this._input.value = '';
	}

	get value() {
		return this._input.value;
	}
}

window.KosTimepicker = KosTimepicker;
